/**
 * kb-docs-tab.tsx — Tab "Tài liệu" của một kho (kiểu HelpKnow): bài viết / tài liệu nạp từ tệp, trang web, Google Docs/Sheets,
 * soạn trực tiếp.
 * Bố cục: thẻ thống kê (bấm để lọc) → báo một lần nếu thiếu khoá AI đọc hiểu → thanh công cụ (lọc nguồn / trạng thái, sắp xếp,
 * menu Quản lý, nút "Thêm tài liệu" gom 3 cách thêm) → thanh thao tác hàng loạt → hai cột:
 *   trái (~280px): tìm, chọn THƯ MỤC (cây Tài liệu riêng, quản lý ở hộp ⚙), "Chọn nhiều", danh sách tài liệu
 *     (biểu tượng loại tệp · tên · chấm trạng thái · số đoạn);
 *   phải: trình đọc tài liệu đang chọn (KbDocReader — các đoạn AI đã học, sửa từng đoạn).
 * Dưới lg: danh sách nằm trên trình đọc.
 */
import { useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  CheckSquare, ChevronDown, FilePlus2, FolderCog, Globe, Loader2, MoreHorizontal, Plus, RefreshCw, Search, Upload, AlertTriangle, MousePointerClick,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/misc'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Loading, ErrorState } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import { currentLocale } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import { PLATFORM_AI_KEYS_PATH } from '@/lib/kb-status'
import {
  useKnowledgeEntries, useKnowledgeCategories, useBackfillKnowledge, useBulkKnowledge, kbLabel, type KnowledgeEntry,
} from '@/hooks/use-knowledge'
import { KbCategoryTree } from './kb-category-tree'
import { KbCategorySelect } from './kb-category-select'
import { KbDocEditDialog } from './kb-doc-edit-dialog'
import { KbDocReader, DocFileIcon } from './kb-doc-reader'
import {
  buildCategoryTree, descendantIds, docFileKind, docStats, docStatusGroup, filterSortDocs, flattenTree,
  DOC_DOT_CLASS, DOC_SORT_LABELS, DOC_SOURCE_LABELS, DOC_STATUS_LABELS, type DocSort, type DocSourceKind, type DocStatusGroup,
  type KnowledgePerms,
} from './kb-lib'
import type { AddKnowledgeTab } from '../add-knowledge-dialog'

const ALL_FOLDERS = '__all__'

/** Màn rộng (≥ lg) → tự mở tài liệu đầu tiên; màn hẹp để người dùng chọn (trình đọc nằm dưới danh sách). */
function useIsWide(): boolean {
  const q = '(min-width: 1024px)'
  const [wide, setWide] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(q).matches)
  useEffect(() => {
    const m = window.matchMedia?.(q)
    if (!m) return
    const on = () => setWide(m.matches)
    m.addEventListener('change', on)
    return () => m.removeEventListener('change', on)
  }, [])
  return wide
}

export function KbDocsTab({ baseId, perms, categoryId, onCategory, onAdd, onSync }: {
  baseId: string
  perms: KnowledgePerms
  categoryId: string
  onCategory: (id: string) => void
  /** Mở hộp thêm (tệp / soạn trực tiếp) với danh mục đang chọn. */
  onAdd: (tab: AddKnowledgeTab) => void
  /** Hộp "Lấy nội dung từ nguồn ngoài" (trang web, Google Docs/Sheets…). */
  onSync: () => void
}) {
  const { t } = useTranslation('aiKnow')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<DocStatusGroup | 'all'>('all')
  const [source, setSource] = useState<DocSourceKind | 'all'>('all')
  const [sort, setSort] = useState<DocSort>('updated_desc')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [selectMode, setSelectMode] = useState(false)
  const [moveTo, setMoveTo] = useState('')
  const [editing, setEditing] = useState<KnowledgeEntry | null>(null)
  const [openId, setOpenId] = useState<string | null>(null)
  const [foldersOpen, setFoldersOpen] = useState(false)
  const wide = useIsWide()
  const { data: cats } = useKnowledgeCategories(baseId, { kind: 'article' })
  const { data: entries, isLoading, isError } = useKnowledgeEntries({ knowledgeBaseId: baseId, format: 'article', preview: '1' }, { pollWhileBusy: true })
  const backfill = useBackfillKnowledge()
  const bulk = useBulkKnowledge()
  const { canCreate, canUpdate, canDelete } = perms
  /** Thao tác hàng loạt cần knowledge.update (bật / tắt / chuyển) hoặc knowledge.delete (xoá). */
  const canBulk = canUpdate || canDelete

  const scope = useMemo(() => (categoryId ? new Set(descendantIds(cats ?? [], categoryId)) : null), [cats, categoryId])
  /** Tài liệu trong danh mục đang chọn (trước khi lọc trạng thái) — nguồn cho thẻ thống kê. */
  const inScope = useMemo(() => (entries ?? []).filter((e) => !scope || (e.categoryId && scope.has(e.categoryId))), [entries, scope])
  const stats = useMemo(() => docStats(inScope), [inScope])
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase()
    const found = q ? inScope.filter((e) => (e.title ?? '').toLowerCase().includes(q) || e.content.toLowerCase().includes(q)) : inScope
    return filterSortDocs(found, { status, source, sort })
  }, [inScope, search, status, source, sort])
  const catName = (id: string | null) => (cats ?? []).find((c) => c.id === id)?.name
  const folders = useMemo(() => flattenTree(buildCategoryTree(cats ?? [])), [cats])
  const filtered = !!search.trim() || status !== 'all' || source !== 'all'
  const allChecked = rows.length > 0 && rows.every((r) => selected.has(r.id))
  const picked = rows.filter((r) => selected.has(r.id)).map((r) => r.id)
  // Tài liệu đang mở: đã chọn (còn trong danh sách) → không thì tài liệu đầu (màn rộng).
  const open = rows.find((r) => r.id === openId) ?? (wide && !openId ? rows[0] : undefined) ?? null

  function toggleOne(id: string) {
    setSelected((s) => { const x = new Set(s); if (x.has(id)) x.delete(id); else x.add(id); return x })
  }
  function resetFilters() { setSearch(''); setStatus('all'); setSource('all') }
  function exitSelect() { setSelected(new Set()); setSelectMode(false) }

  async function reprocess() {
    try {
      const r = await backfill.mutateAsync({ knowledgeBaseId: baseId })
      toast.success(r.queued > 0 ? t('docs.reprocessing', { n: r.queued }) : t('docs.allReady'))
    } catch (err) { toast.error(apiError(err)) }
  }

  async function runBulk(action: 'delete' | 'enable' | 'disable' | 'move') {
    if (!picked.length) return
    if (action === 'delete' && !window.confirm(t('docs.confirmDelete', { n: picked.length }))) return
    try {
      const r = await bulk.mutateAsync({ ids: picked, action, ...(action === 'move' ? { categoryId: moveTo || null } : {}) })
      toast.success(t(`docs.bulkDone.${action}`, { n: r.done }))
      exitSelect()
    } catch (err) { toast.error(apiError(err)) }
  }

  const statCards: { key: DocStatusGroup | 'all'; label: string; value: string; hint: string; tone: string }[] = [
    { key: 'all', label: t('docs.stats.total'), value: `${stats.total}`, hint: t('docs.stats.totalHint', { n: stats.chunks.toLocaleString(currentLocale()) }), tone: 'text-foreground' },
    { key: 'ready', label: t('docs.stats.ready'), value: `${stats.ready}`, hint: t('docs.stats.readyHint'), tone: 'text-success' },
    { key: 'keyword', label: t('docs.stats.keyword'), value: `${stats.keyword}`, hint: t('docs.stats.keywordHint'), tone: 'text-warning' },
    { key: 'issue', label: t('docs.stats.issue'), value: `${stats.issue}`, hint: stats.busy ? t('docs.stats.issueBusy', { n: stats.busy }) : t('docs.stats.issueHint'), tone: 'text-destructive' },
    { key: 'off', label: t('docs.stats.off'), value: `${stats.off}`, hint: t('docs.stats.offHint'), tone: 'text-muted-foreground' },
  ]

  return (
    <div className="min-w-0 space-y-3">
      {/* Thống kê — bấm để lọc theo trạng thái */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-5">
        {statCards.map((c) => (
          <button
            key={c.key} type="button" onClick={() => setStatus(status === c.key ? 'all' : c.key)}
            className={cn('rounded-lg border bg-card px-3 py-2 text-left transition-colors hover:border-primary/50',
              status === c.key && c.key !== 'all' && 'border-primary bg-primary/5')}
          >
            <p className="truncate text-xs text-muted-foreground">{c.label}</p>
            <p className={cn('text-lg font-semibold tabular-nums', c.tone)}>{c.value}</p>
            <p className="truncate text-[11px] text-muted-foreground">{c.hint}</p>
          </button>
        ))}
      </div>

      {stats.keyword > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/5 px-3 py-2 text-xs">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
          <p className="min-w-0">
            <Trans
              t={t} i18nKey="docs.keywordBanner" values={{ n: stats.keyword }}
              components={{
                b: <b />,
                keys: <a href={PLATFORM_AI_KEYS_PATH} target="_blank" rel="noopener" className="font-medium text-primary underline" />,
              }}
            />
          </p>
        </div>
      )}

      {/* Thanh công cụ */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={source} onValueChange={(v) => setSource(v as DocSourceKind | 'all')}>
          <SelectTrigger className="h-9 w-40" aria-label={t('docs.filters.source')}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('docs.filters.allSources')}</SelectItem>
            {(Object.keys(DOC_SOURCE_LABELS) as DocSourceKind[]).map((k) => <SelectItem key={k} value={k}>{DOC_SOURCE_LABELS[k]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => setStatus(v as DocStatusGroup | 'all')}>
          <SelectTrigger className="h-9 w-44" aria-label={t('docs.filters.status')}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('docs.filters.allStatuses')}</SelectItem>
            {(Object.keys(DOC_STATUS_LABELS) as DocStatusGroup[]).map((k) => <SelectItem key={k} value={k}>{DOC_STATUS_LABELS[k]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={(v) => setSort(v as DocSort)}>
          <SelectTrigger className="h-9 w-40" aria-label={t('docs.filters.sort')}><SelectValue /></SelectTrigger>
          <SelectContent>{(Object.keys(DOC_SORT_LABELS) as DocSort[]).map((k) => <SelectItem key={k} value={k}>{DOC_SORT_LABELS[k]}</SelectItem>)}</SelectContent>
        </Select>

        <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
          {canBulk && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline"><MoreHorizontal className="h-4 w-4" /> {t('common:actions.manage')}</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                {canUpdate && (
                  <DropdownMenuItem onSelect={() => void reprocess()} disabled={backfill.isPending}>
                    <RefreshCw className="h-4 w-4" />
                    <span className="flex-1">{t('docs.menu.reprocess')}</span>
                    {stats.total - stats.ready - stats.off > 0 && <Badge variant="warning" className="px-1.5 text-[10px]">{stats.total - stats.ready - stats.off}</Badge>}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={() => { setSelectMode(true); setSelected(new Set(rows.map((r) => r.id))) }} disabled={!rows.length}>
                  <CheckSquare className="h-4 w-4" /> {t('docs.menu.selectAllBulk')}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setFoldersOpen(true)}>
                  <FolderCog className="h-4 w-4" /> {t('docs.menu.manageFolders')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {canCreate && <AddMenu onAdd={onAdd} onSync={onSync} />}
        </div>
      </div>

      {selectMode && picked.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
          <span className="font-medium">{t('docs.bulk.selected', { n: picked.length })}</span>
          {canUpdate && (
            <>
              <Button size="sm" variant="outline" disabled={bulk.isPending} onClick={() => void runBulk('enable')}>{t('docs.bulk.enable')}</Button>
              <Button size="sm" variant="outline" disabled={bulk.isPending} onClick={() => void runBulk('disable')}>{t('docs.bulk.disable')}</Button>
              <span className="flex items-center gap-1">
                <KbCategorySelect knowledgeBaseId={baseId} kind="article" value={moveTo} onChange={setMoveTo} className="h-7 w-48 text-xs" noneLabel={t('docs.bulk.noFolder')} />
                <Button size="sm" variant="outline" disabled={bulk.isPending} onClick={() => void runBulk('move')}>{t('docs.bulk.move')}</Button>
              </span>
            </>
          )}
          {canDelete && <Button size="sm" variant="ghost" className="text-destructive" disabled={bulk.isPending} onClick={() => void runBulk('delete')}>{t('common:actions.delete')}</Button>}
          {bulk.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          <button type="button" className="ml-auto text-xs text-muted-foreground hover:underline" onClick={() => setSelected(new Set())}>{t('docs.bulk.deselect')}</button>
        </div>
      )}

      {isLoading && !entries ? (
        <Loading label={t('docs.loading')} />
      ) : isError ? (
        <ErrorState message={t('docs.loadError')} />
      ) : (entries ?? []).length === 0 ? (
        <EmptyDocs canCreate={canCreate} onAdd={onAdd} onSync={onSync} />
      ) : (
        <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
          {/* Cột trái: tìm · thư mục · danh sách */}
          <aside className="flex min-w-0 flex-col rounded-lg border bg-card lg:sticky lg:top-2 lg:max-h-[calc(100vh-6rem)]" aria-label={t('docs.listLabel')}>
            <div className="space-y-2 border-b p-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input className="h-9 pl-8" placeholder={t('docs.searchPlaceholder')} value={search} onChange={(e) => setSearch(e.target.value)} aria-label={t('docs.searchLabel')} />
              </div>
              <div className="flex items-center gap-1">
                <Select value={categoryId || ALL_FOLDERS} onValueChange={(v) => { onCategory(v === ALL_FOLDERS ? '' : v); setSelected(new Set()) }}>
                  <SelectTrigger className="h-8 min-w-0 flex-1 text-xs" aria-label={t('docs.folder')}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_FOLDERS}>📁 {t('docs.allFolders')}</SelectItem>
                    {folders.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{`${'  '.repeat(c.depth)}${c.depth ? '└ ' : ''}${c.name}${c.entryCount ? ` (${c.entryCount})` : ''}`}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" title={t('docs.manageFolders')} aria-label={t('docs.manageFolders')} onClick={() => setFoldersOpen(true)}>
                  <FolderCog className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                <span>{t('docs.count', { shown: rows.length, total: stats.total })}{filtered && <> · <button type="button" className="text-primary hover:underline" onClick={resetFilters}>{t('docs.clearFilter')}</button></>}</span>
                {selectMode ? (
                  <span className="flex items-center gap-2">
                    <label className="flex items-center gap-1">
                      <Checkbox checked={allChecked} onCheckedChange={() => setSelected(allChecked ? new Set() : new Set(rows.map((r) => r.id)))} aria-label={t('common:actions.selectAll')} /> {t('docs.all')}
                    </label>
                    <button type="button" className="text-primary hover:underline" onClick={exitSelect}>{t('common:actions.done')}</button>
                  </span>
                ) : canBulk ? (
                  <button type="button" className="text-primary hover:underline disabled:opacity-50" disabled={!rows.length} onClick={() => setSelectMode(true)}>{t('docs.selectMany')}</button>
                ) : null}
              </div>
            </div>
            {rows.length === 0 ? (
              <p className="p-4 text-center text-xs text-muted-foreground">
                {filtered ? t('docs.emptyFiltered') : categoryId ? t('docs.emptyFolder') : t('docs.emptyNone')}
              </p>
            ) : (
              <ul className="max-h-80 min-h-0 flex-1 overflow-y-auto p-1 lg:max-h-none">
                {rows.map((e) => {
                  const g = docStatusGroup(e)
                  const active = open?.id === e.id
                  return (
                    <li key={e.id}>
                      <div className={cn('flex items-center gap-2 rounded-md px-2 py-2 text-sm', active ? 'bg-primary/10 text-primary' : 'hover:bg-muted/60', selected.has(e.id) && 'bg-primary/5')}>
                        {selectMode && <Checkbox checked={selected.has(e.id)} onCheckedChange={() => toggleOne(e.id)} aria-label={t('docs.pick', { name: kbLabel(e) })} />}
                        <button type="button" className="flex min-w-0 flex-1 items-center gap-2 text-left" onClick={() => (selectMode ? toggleOne(e.id) : setOpenId(e.id))} title={kbLabel(e)}>
                          <DocFileIcon kind={docFileKind(e)} className="h-4 w-4" />
                          <span className={cn('min-w-0 flex-1 truncate', active && 'font-medium', g === 'off' && 'text-muted-foreground line-through decoration-muted-foreground/40')}>{kbLabel(e)}</span>
                          <span className={cn('h-2 w-2 shrink-0 rounded-full', DOC_DOT_CLASS[g])} title={DOC_STATUS_LABELS[g]} aria-label={DOC_STATUS_LABELS[g]} />
                          <span className="w-8 shrink-0 text-right text-[11px] tabular-nums text-muted-foreground" title={t('docs.chunkCount')}>{(e.chunkCount ?? 0).toLocaleString(currentLocale())}</span>
                        </button>
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </aside>

          {/* Cột phải: trình đọc */}
          {open ? (
            <KbDocReader
              key={open.id} entry={open} perms={perms} folderName={catName(open.categoryId)}
              onEditFull={() => setEditing(open)} onDeleted={() => setOpenId(null)}
            />
          ) : (
            <div className="flex min-h-60 flex-col items-center justify-center gap-2 rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
              <MousePointerClick className="h-6 w-6" />
              <p>{t('docs.pickPrompt')}</p>
              <p className="text-xs">{t('docs.pickHint')}</p>
            </div>
          )}
        </div>
      )}
      {editing && <KbDocEditDialog entry={editing} canEdit={canUpdate} onClose={() => setEditing(null)} />}
      {foldersOpen && (
        <Dialog open onOpenChange={(o) => !o && setFoldersOpen(false)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{t('docs.foldersDialog.title')}</DialogTitle>
              <DialogDescription>{t('docs.foldersDialog.description')}</DialogDescription>
            </DialogHeader>
            <KbCategoryTree
              baseId={baseId} kind="article" selected={categoryId} perms={perms} totalCount={(entries ?? []).length}
              onSelect={(id) => { onCategory(id); setSelected(new Set()); setFoldersOpen(false) }} className="max-h-[60vh] overflow-y-auto"
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

function AddMenu({ onAdd, onSync, align = 'end' }: { onAdd: (tab: AddKnowledgeTab) => void; onSync: () => void; align?: 'end' | 'center' }) {
  const { t } = useTranslation('aiKnow')
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button><Plus className="h-4 w-4" /> {t('docs.add.button')} <ChevronDown className="h-3.5 w-3.5 opacity-80" /></Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-72">
        <AddItem icon={<Upload className="h-4 w-4" />} title={t('docs.add.file')} desc={t('docs.add.fileDesc')} onSelect={() => onAdd('file')} />
        <AddItem icon={<Globe className="h-4 w-4" />} title={t('docs.add.web')} desc={t('docs.add.webDesc')} onSelect={onSync} />
        <AddItem icon={<FilePlus2 className="h-4 w-4" />} title={t('docs.add.text')} desc={t('docs.add.textDesc')} onSelect={() => onAdd('text')} />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** Kho chưa có tài liệu: 3 cách thêm hiện thẳng (không phải mở menu) — chỉ khi có knowledge.create. */
function EmptyDocs({ canCreate, onAdd, onSync }: { canCreate: boolean; onAdd: (tab: AddKnowledgeTab) => void; onSync: () => void }) {
  const { t } = useTranslation('aiKnow')
  const opts = [
    { key: 'file', icon: <Upload className="h-5 w-5" />, title: t('docs.add.file'), desc: t('docs.empty.fileDesc'), onClick: () => onAdd('file') },
    { key: 'web', icon: <Globe className="h-5 w-5" />, title: t('docs.add.web'), desc: t('docs.add.webDesc'), onClick: onSync },
    { key: 'text', icon: <FilePlus2 className="h-5 w-5" />, title: t('docs.add.text'), desc: t('docs.add.textDesc'), onClick: () => onAdd('text') },
  ]
  return (
    <div className="rounded-lg border border-dashed p-8 text-center">
      <p className="font-medium">{t('docs.empty.title')}</p>
      {canCreate && (
        <>
          <p className="mt-1 text-sm text-muted-foreground">{t('docs.empty.hint')}</p>
          <div className="mx-auto mt-5 grid max-w-3xl gap-3 sm:grid-cols-3">
            {opts.map((o) => (
              <button
                key={o.key} type="button" onClick={o.onClick}
                className="flex flex-col items-center gap-2 rounded-lg border bg-card p-4 text-center transition-colors hover:border-primary/50"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">{o.icon}</span>
                <span className="text-sm font-medium">{o.title}</span>
                <span className="text-xs text-muted-foreground">{o.desc}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function AddItem({ icon, title, desc, onSelect }: { icon: React.ReactNode; title: string; desc: string; onSelect: () => void }) {
  return (
    <DropdownMenuItem onSelect={onSelect} className="items-start gap-2.5 py-2">
      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">{icon}</span>
      <span className="min-w-0">
        <span className="block text-sm font-medium">{title}</span>
        <span className="block text-xs text-muted-foreground">{desc}</span>
      </span>
    </DropdownMenuItem>
  )
}
